import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/ThemeProvider";

// Eagerly load landing page for LCP
import Index from "./pages/Index";

// Lazy load other routes to reduce initial bundle size
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Auth = lazy(() => import("./pages/Auth"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Business = lazy(() => import("./pages/Business"));
const CloseDay = lazy(() => import("./pages/CloseDay"));
const History = lazy(() => import("./pages/History"));
const Analytics = lazy(() => import("./pages/Analytics"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Pricing = lazy(() => import("./pages/Pricing"));
const Settings = lazy(() => import("./pages/Settings"));
const Team = lazy(() => import("./pages/Team"));
const Achievements = lazy(() => import("./pages/Achievements"));
const Goals = lazy(() => import("./pages/Goals"));
const Alerts = lazy(() => import("./pages/Alerts"));
const LoanReadiness = lazy(() => import("./pages/LoanReadiness"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Simple loading fallback
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider defaultTheme="light" storageKey="kosh-theme">
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/business/:id" element={<Business />} />
              <Route path="/business/:id/close" element={<CloseDay />} />
              <Route path="/business/:id/history" element={<History />} />
              <Route path="/business/:id/analytics" element={<Analytics />} />
              <Route path="/business/:id/team" element={<Team />} />
              <Route path="/business/:id/achievements" element={<Achievements />} />
              <Route path="/business/:id/goals" element={<Goals />} />
              <Route path="/business/:id/alerts" element={<Alerts />} />
              <Route path="/business/:id/loan-readiness" element={<LoanReadiness />} />
              <Route path="/business/:id/settings" element={<Settings />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
      </TooltipProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
